import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, Images, ArrowRight, MapPin, Building2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BeforeAfter from "@/components/BeforeAfter";
import { api, imgUrl, track, SITE_URL } from "@/lib/api";

const META_TITLE = "Premeny domov pred a po zateplení — reálne realizácie | Zateplenia.eu";
const META_DESC = "Galéria reálnych premien fasád a podhľadov: porovnajte fotky pred a po zateplení od našich overených partnerov z celého Slovenska. Inšpirujte sa a vypočítajte si cenu.";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setMetaProp = (property, content) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

const PremenaCard = ({ r, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
    className="border border-white/10 bg-ink-2 overflow-hidden flex flex-col" data-testid={`premena-card-${index}`}>
    <BeforeAfter before={r.before} after={r.after} alt={`${r.title} — ${r.location}`} testid={`premena-slider-${index}`} height="h-64 md:h-72" />
    <div className="p-4 flex flex-col gap-1.5 flex-1">
      <h2 className="text-white font-semibold text-base">{r.title}</h2>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/50 text-xs">
        {r.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-gold/70" />{r.location}</span>}
        {r.year && <span>{r.year}</span>}
        {r.area > 0 && <span>{r.area} m²</span>}
      </div>
      {r.material && <p className="text-white/60 text-sm">{r.material}</p>}
      {r.partner_name && (
        <p className="text-white/70 text-xs mt-auto pt-2 border-t border-white/5 inline-flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5 text-gold/70" /> Realizoval: <span className="text-gold">{r.partner_name}</span>
        </p>
      )}
    </div>
  </motion.article>
);

export default function Premeny() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = META_TITLE;
    setMeta("description", META_DESC);
    setCanonical(`${SITE_URL}/premeny`);
    setMetaProp("og:title", META_TITLE);
    setMetaProp("og:description", META_DESC);
    setMetaProp("og:type", "website");
    setMetaProp("og:url", `${SITE_URL}/premeny`);
    track("visit", "premeny");
    api.get("/references/before-after").then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!items.length) return;
    setMetaProp("og:image", imgUrl(items[0].after));
    const ld = {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      "name": "Premeny domov pred a po zateplení — Zateplenia.eu",
      "description": META_DESC,
      "url": `${SITE_URL}/premeny`,
      "inLanguage": "sk-SK",
      "image": items.flatMap((r) => [
        { "@type": "ImageObject", "contentUrl": imgUrl(r.before), "name": `${r.title} — pred zateplením` },
        { "@type": "ImageObject", "contentUrl": imgUrl(r.after), "name": `${r.title} — po zateplení` },
      ]),
    };
    let s = document.getElementById("ld-json");
    if (!s) { s = document.createElement("script"); s.id = "ld-json"; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
  }, [items]);

  return (
    <div className="bg-ink min-h-screen" data-testid="premeny-page">
      <Header />

      <section className="pt-36 pb-12">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <div className="inline-flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-6">
            <Images className="w-4 h-4" /> Galéria · Pred / Po
          </div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-white leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="premeny-title">
            Premeny domov<br /><span className="text-gold">pred a po zateplení</span>
          </motion.h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mt-6">
            Reálne realizácie našich partnerov z celého Slovenska. Potiahnite zlatý posuvník a porovnajte
            stav fasády <strong className="text-white">pred a po</strong> — ku každej premene uvádzame lokalitu,
            rok a realizačnú firmu.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
          ) : items.length ? (
            <>
              <p className="text-white/40 text-sm mb-6" data-testid="premeny-count">{items.length} premien s porovnaním pred/po</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="premeny-grid">
                {items.map((r, i) => <PremenaCard key={r.id || i} r={r} index={i} />)}
              </div>
            </>
          ) : (
            <div className="border border-white/10 bg-ink-2 p-12 text-center" data-testid="premeny-empty">
              <Images className="w-10 h-10 text-white/20 mx-auto mb-4" />
              <p className="text-white/60 mb-2">Galériu premien práve pripravujeme — prvé realizácie s fotkami pred/po čakajú na partnerov.</p>
              <p className="text-white/40 text-sm">Ste realizačná firma? <Link to="/stat-sa-partnerom" className="text-gold hover:underline">Pridajte svoje realizácie zadarmo</Link>.</p>
            </div>
          )}

          <div className="mt-14 border border-gold/40 bg-gold/5 p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-white">Chcete takúto premenu aj vy?</h2>
              <p className="text-white/60 text-sm md:text-base mt-2 max-w-xl">
                Vypočítajte si cenu zateplenia na mieru — materiál aj ponuky overených firiem z vášho okresu, zdarma a nezáväzne.
              </p>
            </div>
            <Link to="/#kalkulacka" data-testid="premeny-cta-calc"
              className="shrink-0 bg-gold text-ink px-8 py-4 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors flex items-center gap-2">
              Vypočítať cenu <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/partneri" data-testid="premeny-cta-partners"
              className="shrink-0 border border-gold/50 text-gold px-8 py-4 font-bold uppercase tracking-wider hover:bg-gold hover:text-ink transition-colors">
              Naši partneri
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
